import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import DropDownField from "../../../components/Editing/DropdownField";
import { DropdownOption } from "../../../utils/pdApiUtils";
import { EditActions, editActions } from "../../../actions/editActions";
import { PdApiActions, pdApiActions } from "../../../actions/pdApiActions";
import { Button, Form } from "semantic-ui-react";
import styles from "./Translation.module.scss";
import windowsLogo from "../../../images/windowsLogo.svg";
import macLogo from "../../../images/macLogo.svg";

// Declare the software download links
const windowsDownloadLink =
  "https://github.com/lelegard/qtlinguist-installers/releases/download/v5.12.0/QtLinguist-5.12.0.exe";
const macDownloadLink =
  "https://github.com/lelegard/qtlinguist-installers/releases/download/v5.12.0/QtLinguist-5.12.0.dmg";

type StateProps = {
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
};

type TranslationState = {
  selectedLocale: string;
  selectedDomainGroup: string;
};

export type TranslationProps = StateProps & DispatchProps;

export class Translation extends React.Component<TranslationProps, TranslationState> {
  state = {
    selectedLocale: "",
    selectedDomainGroup: ""
  };

  render(): JSX.Element {
    const { pdApi } = this.props;
    const { domainGroups, locales } = pdApi;
    const { selectedLocale, selectedDomainGroup } = this.state;

    const languageOptions: DropdownOption[] = locales.map(locale => ({
      text: locale.comment,
      value: locale.locale
    }));

    // Don't allow getting localizations for these domain groups

    const forbiddenDomainGroups = ["Unknown"];

    // TODO: Improve object.keys
    // See: https://fettblog.eu/typescript-better-object-keys/
    const domainGroupOptions: DropdownOption[] = Object.keys(domainGroups)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((key: any) => !forbiddenDomainGroups.includes(domainGroups[key].enumName))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((key: any) => ({
        text: domainGroups[key].enumName,
        value: domainGroups[key].enumName
      }));

    domainGroupOptions.push({
      text: "UI Translations",
      value: "UITranslations"
    });

    const selectionsNotDone = !selectedLocale || !selectedDomainGroup;

    return (
      <div className={styles.containerWithPadding}>
        <div className={styles.step}>
          <div className={styles.introHeader}>Strings translations</div>
          <div className={styles.introText}>
            Please use the following process to localize KCE Tools content and UI texts.
          </div>
        </div>

        <div className={styles.step}>
          <span className={styles.stepNumber}>1</span>
          <span className={styles.stepTitle}>Install QT Linquist</span>
        </div>
        <div className={styles.stepContainer}>
          <span className={styles.stepParagraph}>
            QT Linquist is an open source application for translating texts. You need to be able to
            open the files containing the texts to be translated.
          </span>
          <br />
          <span className={styles.stepParagraph}>
            Download and install the application for your operating system to get started.
          </span>
          <div className={styles.stepButtonRow}>
            <Button
              style={{ marginRight: "10px" }}
              onClick={() => {
                window.open(windowsDownloadLink);
              }}
            >
              <img src={windowsLogo} alt="WinLogo" />
              <span>Download for Windows</span>
            </Button>
            <Button
              onClick={() => {
                window.open(macDownloadLink);
              }}
            >
              <img src={macLogo} alt="AppleLogo" />
              <span>Download for MacOS</span>
            </Button>
          </div>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNumber}>2</span>
          <span className={styles.stepTitle}>Get texts to translate</span>
        </div>
        <div className={styles.stepContainer}>
          <span className={styles.stepParagraph}>
            Please, select the texts you are about to translate and click Download Texts.
          </span>
          <Form className={styles.stepDropdownRow}>
            <div style={{ margin: "0 20px 0 0" }}>
              <label>Language</label>
              <DropDownField
                id="language"
                placeholder="Please select..."
                value={selectedLocale}
                options={languageOptions}
                onChange={e => this.changeLanguage(e)}
              />
            </div>
            <div>
              <label>Texts to translate</label>
              <DropDownField
                id="domainGroup"
                placeholder="Please select..."
                value={selectedDomainGroup}
                options={domainGroupOptions}
                onChange={e => this.changeDomainGroup(e)}
              />
            </div>
          </Form>
          <div className={styles.stepButtonRow}>
            <Button
              id="DownloadTextsButton"
              className={styles.Translations}
              onClick={this.downloadTexts}
              disabled={selectionsNotDone}
            >
              Download texts
            </Button>
          </div>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNumber}>3</span>
          <span className={styles.stepTitle}>Translate and deliver</span>
        </div>
        <div className={styles.stepContainer}>
          <span className={styles.stepParagraph}>
            Open the downloaded file with QT Linquist and translate the texts. When you are done,
            click Deliver Translated Texts and select the file you translated.
          </span>
        </div>

        <div className={styles.lastStepContainer}>
          <span className={styles.buttondivider} />
          <Button
            id="DeliverTranslatedTextsButton"
            className={styles.actionbutton}
            as="label"
            htmlFor="upload"
            type="button"
          >
            Deliver translated texts
          </Button>
          <input
            type="file"
            id="upload"
            style={{ display: "none" }}
            onChange={e => this.uploadTexts(e)}
          />
        </div>
      </div>
    );
  }

  private uploadTexts = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    try {
      // Reading the file might cause exceptions from the browser on a rainy day
      const fileContent: ArrayBuffer = await this.readFile(event);
      this.props.editActions.addTranslations(fileContent);
    } catch (error) {
      // If we actually sometime end up in here, we could hook up the redux-notification system and give a prettier error.
      alert(
        "Something unexpected happened when trying to read the file. Please refresh the page and try again."
      );
      console.error(error);
    }
  };

  // Takes in the event as argument and returns the file content as array buffer.
  // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/ArrayBuffer
  // See: https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsArrayBuffer
  private readFile = (event: React.ChangeEvent<HTMLInputElement>): Promise<ArrayBuffer> => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const file = event.target.files![0];

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // File read success
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      reader.onload = (event: any) => {
        resolve(event.target.result);
      };

      // File read error
      reader.onerror = error => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  private downloadTexts = async (): Promise<void> => {
    const { selectedLocale, selectedDomainGroup } = this.state;
    this.props.pdApiActions.downloadLocalizationFile(selectedDomainGroup, selectedLocale);
  };

  private changeDomainGroup = (newDomainGroup: string): void => {
    this.setState(prevState => ({
      ...prevState,
      selectedDomainGroup: newDomainGroup
    }));
  };

  private changeLanguage = (newLocale: string): void => {
    this.setState(prevState => ({
      ...prevState,
      selectedLocale: newLocale
    }));
  };
}

const mapStateToProps = (state: State.Root) => ({
  pdApi: state.pdApi
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | PdApiActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Translation);

